import React, { useCallback, useEffect, useMemo, useState } from "react";
import LoadError from "../../components/admin/LoadError";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import RefreshIcon from "@mui/icons-material/Refresh";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CodeIcon from "@mui/icons-material/Code";
import { toast } from "react-toastify";
import api from "../../api";

const CATEGORY_LABELS = {
  marking: "Marking",
  ocr: "OCR / handwriting",
  extraction: "PDF import / extraction",
  generation: "Generation",
  explanations: "Explanations / tutoring",
  explanation_video: "Explanation video",
  images: "Images / diagrams",
  assistants: "Assistants / other",
};

const categoryLabel = (c) => CATEGORY_LABELS[c] || c;

const formatDateTime = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return value;
  }
};

const copyText = async (text, what) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    toast.success(`${what} copied.`);
  } catch {
    toast.error(`Could not copy ${what.toLowerCase()}.`);
  }
};

const EXTRACTION_LABELS = {
  partial: "Partly extracted",
  source_only: "Source code only",
};

// ---- readable prompt rendering -------------------------------------------------------

const VarChip = ({ seg }) => (
  <Tooltip title={<span style={{ fontFamily: "monospace", whiteSpace: "pre-wrap" }}>{seg.expr}</span>} arrow>
    <Chip
      component="span"
      size="small"
      label={seg.label}
      color={seg.ref ? "secondary" : "primary"}
      variant="outlined"
      sx={{ height: 20, mx: 0.25, fontSize: 12, fontWeight: 600, verticalAlign: "baseline", bgcolor: seg.ref ? "#f3e5f5" : "#e3f2fd", "& .MuiChip-label": { px: 0.75 } }}
    />
  </Tooltip>
);

function InlineSegments({ segments }) {
  return (
    <>
      {(segments || []).map((seg, i) => {
        if (seg.type === "text") return <React.Fragment key={i}>{seg.text}</React.Fragment>;
        if (seg.type === "choice") return <ChoiceSegment key={i} seg={seg} />;
        return <VarChip key={i} seg={seg} />;
      })}
    </>
  );
}

function ChoiceSegment({ seg }) {
  return (
    <Tooltip title={<span style={{ fontFamily: "monospace", whiteSpace: "pre-wrap" }}>{seg.expr}</span>} arrow>
      <Box component="span" sx={{ bgcolor: "#fff3e0", border: "1px dashed #ffb74d", borderRadius: 1, px: 0.5, mx: 0.25, whiteSpace: "pre-wrap" }}>
        {(seg.options || []).map((o, i) => (
          <React.Fragment key={i}>
            {i > 0 && <Box component="span" sx={{ color: "text.disabled", mx: 0.5 }}>|</Box>}
            <Box component="span" sx={{ fontSize: 11, fontWeight: 700, color: "warning.dark", mr: 0.5 }}>{o.when}:</Box>
            {o.segments && o.segments.length ? <InlineSegments segments={o.segments} /> : <em>(nothing)</em>}
          </React.Fragment>
        ))}
      </Box>
    </Tooltip>
  );
}

/** Split segments into lines; each line is a list of inline segments. */
const toLines = (segments) => {
  const lines = [[]];
  (segments || []).forEach((seg) => {
    if (seg.type !== "text") {
      lines[lines.length - 1].push(seg);
      return;
    }
    seg.text.split("\n").forEach((piece, i) => {
      if (i > 0) lines.push([]);
      if (piece !== "") lines[lines.length - 1].push({ type: "text", text: piece });
    });
  });
  return lines;
};

const lineText = (line) => line.map((s) => (s.type === "text" ? s.text : "")).join("");

const LIST_RE = /^(\s*)((?:\d+|[a-zA-Z])[.)]|[-*•–])\s+/;

const PromptLine = ({ line }) => {
  const text = lineText(line);
  if (!line.length || (text.trim() === "" && line.every((s) => s.type === "text"))) {
    return <Box sx={{ height: 10 }} />;
  }
  const first = line[0];
  const list = first.type === "text" ? first.text.match(LIST_RE) : null;
  if (list) {
    const indent = Math.min(6, Math.floor(list[1].replace(/\t/g, "    ").length / 2));
    const rest = [{ type: "text", text: first.text.slice(list[0].length) }, ...line.slice(1)];
    return (
      <Box sx={{ display: "flex", pl: 1 + indent * 1.5, my: 0.25 }}>
        <Box component="span" sx={{ minWidth: 22, flexShrink: 0, color: "text.secondary", fontWeight: 600 }}>
          {/^[-*•–]$/.test(list[2]) ? "•" : list[2]}
        </Box>
        <Box component="span" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}><InlineSegments segments={rest} /></Box>
      </Box>
    );
  }
  const trimmed = text.trim();
  const letters = trimmed.replace(/[^A-Za-z]/g, "");
  const allCaps = letters.length >= 3 && letters === letters.toUpperCase() && trimmed.length <= 120;
  const heading = line.every((s) => s.type === "text") && (allCaps || (trimmed.endsWith(":") && trimmed.length <= 90));
  if (heading) {
    return (
      <Typography component="div" variant="subtitle2" sx={{ fontWeight: 700, mt: 1.25, mb: 0.25, color: "#37474f", letterSpacing: allCaps ? 0.3 : 0 }}>
        {trimmed}
      </Typography>
    );
  }
  return (
    <Box sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
      <InlineSegments segments={line} />
    </Box>
  );
};

const PromptPart = ({ part, showLabel }) => {
  const lines = useMemo(() => toLines(part.segments), [part.segments]);
  return (
    <Box sx={{ mb: 2.5 }}>
      {showLabel && (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.75, pb: 0.5, borderBottom: "1px solid #ece6d6" }}>
          <Typography variant="overline" sx={{ fontWeight: 800, lineHeight: 1.6, color: "#5d4037" }}>{part.label}</Typography>
          {part.note && <Chip size="small" color={part.conditional ? "warning" : "default"} variant="outlined" label={part.note} sx={{ height: "auto", "& .MuiChip-label": { whiteSpace: "normal", py: 0.25 } }} />}
          {part.line && <Typography variant="caption" color="text.disabled">line {part.line}</Typography>}
        </Stack>
      )}
      {!showLabel && part.note && (
        <Chip size="small" color="warning" variant="outlined" label={part.note} sx={{ mb: 1, height: "auto", "& .MuiChip-label": { whiteSpace: "normal", py: 0.25 } }} />
      )}
      <Box sx={{ fontSize: 14.5, lineHeight: 1.65, color: "#212121" }}>
        {lines.map((line, i) => <PromptLine key={i} line={line} />)}
      </Box>
    </Box>
  );
};

const SourceBox = ({ text }) => (
  <Box
    component="pre"
    sx={{
      // width 0 + minWidth 100%: the pre fills the cell but never widens the table; it scrolls itself
      width: 0, minWidth: "100%", boxSizing: "border-box",
      m: 0, p: 2, maxHeight: 520, overflow: "auto", bgcolor: "#1e1e1e", color: "#d4d4d4",
      fontFamily: "Consolas, Monaco, 'Courier New', monospace", fontSize: 12.5, lineHeight: 1.5,
      borderRadius: 1, whiteSpace: "pre", tabSize: 4,
    }}
  >
    {text}
  </Box>
);

const PromptDetail = ({ detail, location }) => {
  const prompt = detail.prompt || { parts: [], variables: [] };
  const parts = prompt.parts || [];
  const sourceOnly = detail.extraction === "source_only" || parts.length === 0;
  const [showSource, setShowSource] = useState(sourceOnly);
  const source = detail.source ?? detail.text;
  const showLabels = parts.length > 1 || (parts[0] && parts[0].label !== "Prompt");

  return (
    <>
      {detail.summary && (
        <Box sx={{ mb: 1.5, p: 1.5, bgcolor: "#e8f5e9", borderLeft: "4px solid #66bb6a", borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>What this prompt does</Typography>
          <Typography variant="body2">{detail.summary}</Typography>
        </Box>
      )}
      {detail.prompt_note && <Alert severity="info" sx={{ mb: 1.5 }}>{detail.prompt_note}</Alert>}
      {sourceOnly && <Alert severity="warning" sx={{ mb: 1.5 }}>Couldn&apos;t extract this prompt cleanly — showing source code.</Alert>}
      {detail.extraction === "partial" && !sourceOnly && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>Partly extracted: {detail.extraction_note || "some wording is built by code."} Check the source code for the full picture.</Alert>
      )}

      <Stack direction="row" spacing={1} sx={{ mb: 1 }} alignItems="center" flexWrap="wrap" useFlexGap>
        {!sourceOnly && (
          <Button size="small" variant="outlined" startIcon={<ContentCopyIcon />} onClick={() => copyText(prompt.readable || "", "Prompt")}>Copy prompt</Button>
        )}
        <Button size="small" variant="outlined" startIcon={<ContentCopyIcon />} onClick={() => copyText(location, "Path")}>Copy path:line</Button>
        <Button size="small" variant={showSource ? "contained" : "outlined"} color="inherit" startIcon={<CodeIcon />} onClick={() => setShowSource((v) => !v)}>
          {showSource ? "Hide source code" : "Show source code"}
        </Button>
        <Typography variant="caption" color="text.secondary">
          {detail.prompt_from ? `prompt text from ${detail.prompt_from} · ` : ""}
          {detail.class ? `${detail.class} · ` : ""}lines {detail.line}–{detail.end_line}
        </Typography>
      </Stack>

      {!sourceOnly && (
        <Paper
          variant="outlined"
          sx={{
            width: 0, minWidth: "100%", boxSizing: "border-box",
            p: { xs: 2, md: 3 }, mb: 1.5, maxHeight: 640, overflow: "auto",
            bgcolor: "#fffdf7", borderColor: "#e8e0cc", boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          }}
        >
          {parts.map((part, i) => <PromptPart key={i} part={part} showLabel={showLabels} />)}
        </Paper>
      )}

      {!sourceOnly && (prompt.variables || []).length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Variables used</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
            Filled in when the prompt runs. Hover a highlighted value in the prompt to see the code it comes from.
          </Typography>
          <Stack spacing={0.5}>
            {prompt.variables.map((v) => (
              <Stack key={v.expr} direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                <VarChip seg={v} />
                <Typography variant="caption" sx={{ fontFamily: "monospace", color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }} title={v.expr}>
                  {v.expr.replace(/\s+/g, " ")}
                </Typography>
                {v.ref && <Typography variant="caption" color="secondary.main">(shown as its own section above)</Typography>}
              </Stack>
            ))}
          </Stack>
        </Box>
      )}

      <Collapse in={showSource} unmountOnExit>
        <SourceBox text={source} />
      </Collapse>
    </>
  );
};

const FlagChips = ({ flags }) => (
  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
    {flags?.uses_config && <Chip size="small" variant="outlined" color="info" label="config" />}
    {flags?.uses_db && <Chip size="small" variant="outlined" color="secondary" label="DB content" />}
    {flags?.env_override && <Chip size="small" variant="outlined" color="warning" label=".env override" />}
  </Stack>
);

const PromptRow = ({ row, open, onToggle, detail, setDetail }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const notFound = row.status !== "ok";

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`admin/ai-prompts/${encodeURIComponent(row.id)}`);
      if (res.data?.success) setDetail(row.id, res.data.data);
      else setError(res.data?.message || "Failed to load prompt.");
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to load prompt.");
    } finally {
      setLoading(false);
    }
  }, [row.id, setDetail]);

  const toggle = () => {
    const next = !open;
    onToggle(row.id, next);
    if (next && !detail && !loading) load();
  };

  const location = detail?.location || row.location;

  return (
    <>
      <TableRow hover sx={{ "& > *": { borderBottom: "unset" }, ...(notFound ? { bgcolor: "#fff8e1" } : {}) }}>
        <TableCell padding="checkbox">
          <IconButton size="small" onClick={toggle} aria-label="expand row">
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={600}>{row.title}</Typography>
          <Typography variant="caption" color="text.secondary">{row.purpose}</Typography>
          {row.extraction && row.extraction !== "clean" && (
            <Tooltip title={row.extraction_note || ""}>
              <Chip size="small" variant="outlined" color="warning" label={EXTRACTION_LABELS[row.extraction] || row.extraction} sx={{ ml: 1, height: 18, fontSize: 11 }} />
            </Tooltip>
          )}
          {notFound && (
            <Typography variant="caption" color="warning.main" display="block">
              ⚠ Source not found{row.message ? `: ${row.message}` : ""}
            </Typography>
          )}
        </TableCell>
        <TableCell><Chip size="small" label={categoryLabel(row.category)} /></TableCell>
        <TableCell><Typography variant="body2">{row.provider}</Typography></TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontFamily: "monospace", wordBreak: "break-all", userSelect: "all" }}>
            {row.location}
          </Typography>
          {row.member && (
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
              {row.member}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <Tooltip title={formatDateTime(row.modified_at)}>
            <Typography variant="body2">{row.modified_human || "—"}</Typography>
          </Tooltip>
        </TableCell>
        <TableCell><FlagChips flags={row.flags} /></TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ py: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ my: 2 }}>
              {loading && (
                <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={18} /><Typography variant="body2">Loading prompt…</Typography></Stack>
              )}
              {error && <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>{error}</Alert>}
              {detail && detail.status !== "ok" && (
                <Alert severity="warning">Source not found{detail.message ? `: ${detail.message}` : ""}. Nothing is shown rather than stale text.</Alert>
              )}
              {detail && detail.status === "ok" && <PromptDetail detail={detail} location={location} />}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const AiPrompts = () => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [provider, setProvider] = useState("");
  const [flag, setFlag] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [expanded, setExpanded] = useState({});
  const [details, setDetails] = useState({});

  const toggleExpanded = useCallback((id, value) => setExpanded((prev) => ({ ...prev, [id]: value })), []);
  const setDetail = useCallback((id, value) => setDetails((prev) => ({ ...prev, [id]: value })), []);

  const fetchPrompts = async (refresh = false) => {
    try {
      setLoading(true);
      setLoadError(null);
      const res = await api.get("admin/ai-prompts", { params: refresh ? { refresh: 1 } : {} });
      if (res.data?.success) {
        setRows(res.data.data || []);
        setMeta(res.data.meta || null);
        setDetails({});
        if (refresh) toast.success("Prompts re-read from the source files.");
      }
    } catch (e) {
      setLoadError(e || true);
      toast.error(e?.response?.data?.message || "Failed to load AI prompts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrompts();
  }, []);

  const providerOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => (r.provider || "").split(/\s*(?:,|\/|→|\(|\)|\+)\s*/).forEach((p) => {
      const v = p.trim();
      if (v.length > 2) set.add(v);
    }));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const categoryCounts = useMemo(() => {
    const counts = {};
    rows.forEach((r) => { counts[r.category] = (counts[r.category] || 0) + 1; });
    return counts;
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (category && r.category !== category) return false;
      if (provider && !(r.provider || "").includes(provider)) return false;
      if (flag === "config" && !r.flags?.uses_config) return false;
      if (flag === "db" && !r.flags?.uses_db) return false;
      if (flag === "env" && !r.flags?.env_override) return false;
      if (flag === "not_found" && r.status === "ok") return false;
      if (flag === "partial" && r.extraction !== "partial") return false;
      if (flag === "source_only" && r.extraction !== "source_only") return false;
      if (!q) return true;
      return [r.title, r.purpose, r.summary, r.location, r.member, r.class, r.preview, r.search_text]
        .some((v) => String(v || "").toLowerCase().includes(q));
    });
  }, [rows, search, category, provider, flag]);

  useEffect(() => {
    setPage(0);
  }, [search, category, provider, flag, rowsPerPage]);

  const maxPage = Math.max(0, Math.ceil(filtered.length / rowsPerPage) - 1);
  const currentPage = Math.min(page, maxPage);
  const pageStart = currentPage * rowsPerPage;
  const pageRows = filtered.slice(pageStart, pageStart + rowsPerPage);
  const rangeText = filtered.length === 0
    ? `Showing 0 of ${rows.length} entries`
    : `Showing ${pageStart + 1}–${pageStart + pageRows.length} of ${filtered.length}`
      + (filtered.length !== rows.length ? ` filtered (${rows.length} total)` : "") + " entries";

  const notFoundCount = rows.filter((r) => r.status !== "ok").length;

  return (
    <Box sx={{ p: 3, minWidth: 0, maxWidth: "100%", boxSizing: "border-box", minHeight: "100vh", background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)" }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} sx={{ mb: 1 }}>
        <Typography variant="h5" fontWeight={800}>AI Prompts</Typography>
        <Button variant="contained" startIcon={<RefreshIcon />} onClick={() => fetchPrompts(true)} disabled={loading}>
          Refresh
        </Button>
      </Stack>
      <Alert severity="info" sx={{ mb: 2 }}>
        Read-only view of the AI prompts in the backend source code. Open a row to read the prompt as plain text:
        highlighted values (e.g. <b>Question text</b>) are filled in when the prompt runs, and sections marked
        &quot;Only included if …&quot; are added only in that case. Use &quot;Show source code&quot; to see the code, and edit the file
        shown in the File column to change a prompt. Values from config or .env are not evaluated here.
      </Alert>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }} sx={{ mb: 1.5 }}>
          <TextField size="small" label="Search title, purpose, file or prompt text" value={search}
            onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 300, flex: 1 }} />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Category</InputLabel>
            <Select label="Category" value={category} onChange={(e) => setCategory(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {Object.keys(categoryCounts).map((c) => (
                <MenuItem key={c} value={c}>{categoryLabel(c)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Provider</InputLabel>
            <Select label="Provider" value={provider} onChange={(e) => setProvider(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {providerOptions.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Flags</InputLabel>
            <Select label="Flags" value={flag} onChange={(e) => setFlag(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="config">Uses config</MenuItem>
              <MenuItem value="db">Uses DB content</MenuItem>
              <MenuItem value="env">.env override</MenuItem>
              <MenuItem value="not_found">Source not found</MenuItem>
              <MenuItem value="partial">Partly extracted</MenuItem>
              <MenuItem value="source_only">Source code only</MenuItem>
            </Select>
          </FormControl>
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          <Chip label={`All (${rows.length})`} color={category === "" ? "primary" : "default"} onClick={() => setCategory("")} />
          {Object.entries(categoryCounts).map(([c, n]) => (
            <Chip key={c} label={`${categoryLabel(c)} (${n})`} color={category === c ? "primary" : "default"} onClick={() => setCategory(c)} />
          ))}
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" color="text.secondary">
            {rangeText}
            {meta?.cached_at ? ` · read from source ${formatDateTime(meta.cached_at)}` : ""}
          </Typography>
        </Stack>
      </Paper>

      {notFoundCount > 0 && !loading && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {notFoundCount} registered prompt{notFoundCount > 1 ? "s were" : " was"} not found in the source (the code may have moved).
        </Alert>
      )}

      {loadError ? (
        <LoadError what="AI prompts" error={loadError} onRetry={() => fetchPrompts()} />
      ) : (
        <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer sx={{ overflowX: "auto", maxHeight: "calc(100vh - 220px)", minHeight: 300 }}>
          <Table size="small" stickyHeader sx={{ minWidth: 1100 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 48 }} />
                <TableCell sx={{ minWidth: 260 }}>Title</TableCell>
                <TableCell sx={{ minWidth: 140 }}>Category</TableCell>
                <TableCell sx={{ minWidth: 150 }}>Provider / model</TableCell>
                <TableCell sx={{ minWidth: 240 }}>File:line</TableCell>
                <TableCell sx={{ minWidth: 110 }}>Modified</TableCell>
                <TableCell sx={{ minWidth: 150 }}>Flags</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>No prompts match these filters.</TableCell></TableRow>
              ) : (
                pageRows.map((row) => (
                  <PromptRow key={row.id} row={row} open={!!expanded[row.id]} onToggle={toggleExpanded}
                    detail={details[row.id] || null} setDetail={setDetail} />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filtered.length}
          page={currentPage}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: 1, borderColor: "divider" }}
        />
        </Paper>
      )}
    </Box>
  );
};

export default AiPrompts;
